import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import QuizPreviewModal from './QuizPreviewModal';

const LANGUAGE_OPTIONS = ['ko', 'en', 'ja', 'zh'];
const QUIZ_TYPE_OPTIONS = ['OX', 'INITIAL'];

const emptySet = {
  quiz_date: '',
  title: '',
  source_scope: '',
  wrong_guide_default: '',
  language: 'ko',
  qt_ymd: '',
  is_active: 'Y',
};

const createTempId = () => `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const toYmd = (value) => (value ? String(value).replace(/-/g, '').slice(0, 8) : '');

const DUPLICATE_SET_MESSAGE =
  '해당 퀴즈 일자와 언어 조합에 이미 등록된 퀴즈가 있습니다. 목록에서 기존 항목을 수정하거나 다른 일자·언어를 선택해주세요.';

const formatSaveError = (error) => {
  if (error?.code === '23505') return DUPLICATE_SET_MESSAGE;
  return error?.message || '저장 중 오류가 발생했습니다.';
};

const QuizSetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [quizSet, setQuizSet] = useState(emptySet);
  const [questions, setQuestions] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isNew) {
      setQuizSet(emptySet);
      setQuestions([]);
      setDeletedQuestionIds([]);
      setLoading(false);
      return;
    }
    loadData();
  }, [id, isNew]);

  const loadData = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [{ data: setData, error: setError }, { data: questionData, error: questionError }] = await Promise.all([
        supabase
          .from('quiz_sets')
          .select('id, quiz_date, title, source_scope, wrong_guide_default, language, qt_ymd, is_active')
          .eq('id', id)
          .single(),
        supabase
          .from('quiz_questions')
          .select(
            'id, question_no, question_type, question_text, initial_hint, correct_answer, explanation, wrong_guide, display_order, language, qt_ymd'
          )
          .eq('quiz_set_id', id)
          .order('question_no', { ascending: true }),
      ]);

      if (setError) throw setError;
      if (questionError) throw questionError;

      setQuizSet({
        ...setData,
        quiz_date: setData.quiz_date || '',
      });
      setQuestions(questionData ?? []);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || '데이터 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSetChange = (event) => {
    const { name, value } = event.target;
    setQuizSet((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'quiz_date') {
        next.qt_ymd = toYmd(value);
        setQuestions((prevQuestions) =>
          prevQuestions.map((question) => ({
            ...question,
            qt_ymd: next.qt_ymd,
          }))
        );
      }
      return next;
    });
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prev) => {
      const copied = [...prev];
      copied[index] = { ...copied[index], [field]: value };
      return copied;
    });
  };

  const handleAddQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        tempId: createTempId(),
        question_no: prev.length + 1,
        question_type: 'OX',
        question_text: '',
        initial_hint: '',
        correct_answer: '',
        explanation: '',
        wrong_guide: '',
        display_order: prev.length + 1,
        language: quizSet.language,
        qt_ymd: quizSet.qt_ymd,
      },
    ]);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions((prev) => {
      const target = prev[index];
      if (target?.id) setDeletedQuestionIds((prevIds) => [...prevIds, target.id]);
      const copied = [...prev];
      copied.splice(index, 1);
      return copied.map((item, idx) => ({
        ...item,
        question_no: idx + 1,
        display_order: idx + 1,
      }));
    });
  };

  const resolveSetQuizType = () => {
    const types = [...new Set(questions.map((question) => question.question_type).filter(Boolean))];
    if (types.length === 0) return 'MIXED';
    if (types.length === 1) return types[0];
    return 'MIXED';
  };

  const setPayload = useMemo(
    () => ({
      quiz_date: quizSet.quiz_date,
      quiz_type: resolveSetQuizType(),
      title: quizSet.title?.trim(),
      source_scope: quizSet.source_scope?.trim() || null,
      wrong_guide_default: quizSet.wrong_guide_default?.trim() || null,
      language: quizSet.language,
      qt_ymd: quizSet.qt_ymd,
      is_active: quizSet.is_active || 'Y',
    }),
    [quizSet, questions]
  );

  const validate = () => {
    if (!setPayload.quiz_date || !setPayload.title || !setPayload.language || !setPayload.qt_ymd) {
      setMessage({ type: 'error', text: '퀴즈 일자, 제목, 언어, qt_ymd는 필수입니다.' });
      return false;
    }
    if (questions.length === 0) {
      setMessage({ type: 'error', text: '문항을 1개 이상 등록해주세요.' });
      return false;
    }
    const invalidType = questions.find(
      (question) => !QUIZ_TYPE_OPTIONS.includes(question.question_type)
    );
    if (invalidType) {
      setMessage({ type: 'error', text: '각 문항의 유형(OX/INITIAL)을 선택해주세요.' });
      return false;
    }
    const invalidQuestion = questions.find(
      (question) => !String(question.question_text || '').trim() || !String(question.correct_answer || '').trim()
    );
    if (invalidQuestion) {
      setMessage({ type: 'error', text: '각 문항의 문제 내용과 정답은 필수입니다.' });
      return false;
    }
    return true;
  };

  const findExistingSetId = async (quizDate, language) => {
    const { data, error } = await supabase
      .from('quiz_sets')
      .select('id')
      .eq('quiz_date', quizDate)
      .eq('language', language)
      .maybeSingle();
    if (error) throw error;
    return data?.id ?? null;
  };

  const syncQuestions = async (savedSetId) => {
    const prepared = questions.map((question, index) => ({
      id: question.id,
      tempId: question.tempId,
      quiz_set_id: savedSetId,
      question_no: Number(question.question_no) || index + 1,
      question_type: question.question_type,
      question_text: question.question_text?.trim(),
      initial_hint: question.initial_hint?.trim() || null,
      correct_answer: question.correct_answer?.trim(),
      explanation: question.explanation?.trim() || null,
      wrong_guide: question.wrong_guide?.trim() || null,
      display_order: Number(question.display_order) || index + 1,
      language: quizSet.language,
      qt_ymd: quizSet.qt_ymd,
    }));

    const toInsert = prepared.filter((question) => !question.id);
    const toUpsert = prepared.filter((question) => question.id).map(({ tempId, ...rest }) => rest);

    if (toInsert.length > 0) {
      const insertPayload = toInsert.map(({ id: ignoredId, tempId, ...rest }) => rest);
      const { error } = await supabase.from('quiz_questions').insert(insertPayload);
      if (error) throw error;
    }

    if (toUpsert.length > 0) {
      const { error } = await supabase.from('quiz_questions').upsert(toUpsert);
      if (error) throw error;
    }

    if (deletedQuestionIds.length > 0) {
      const { error } = await supabase.from('quiz_questions').delete().in('id', deletedQuestionIds);
      if (error) throw error;
      setDeletedQuestionIds([]);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage(null);
    try {
      let savedSetId = id;
      let reusedExistingSet = false;
      const existingSetId = await findExistingSetId(setPayload.quiz_date, setPayload.language);

      if (isNew) {
        if (existingSetId) {
          const { error } = await supabase.from('quiz_sets').update(setPayload).eq('id', existingSetId);
          if (error) throw error;
          savedSetId = existingSetId;
          reusedExistingSet = true;
          navigate(`/quiz/${savedSetId}`, { replace: true });
        } else {
          const { data, error } = await supabase.from('quiz_sets').insert([setPayload]).select().single();
          if (error) throw error;
          savedSetId = data.id;
          navigate(`/quiz/${savedSetId}`, { replace: true });
        }
      } else {
        if (existingSetId && existingSetId !== id) {
          setMessage({ type: 'error', text: DUPLICATE_SET_MESSAGE });
          return;
        }
        const { error } = await supabase.from('quiz_sets').update(setPayload).eq('id', id);
        if (error) throw error;
      }

      await syncQuestions(savedSetId);
      if (!isNew) {
        await loadData();
      }
      setMessage({
        type: 'success',
        text: reusedExistingSet
          ? '해당 일자·언어에 기존 퀴즈가 있어 그 항목을 수정했습니다.'
          : '저장되었습니다.',
      });
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: formatSaveError(error) });
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    const previewable = questions.filter((q) => String(q.question_text || '').trim());
    if (previewable.length === 0) {
      setMessage({ type: 'error', text: '미리볼 문항이 없습니다. 문제 내용을 입력한 뒤 다시 시도해주세요.' });
      return;
    }
    setShowPreview(true);
  };

  if (loading) return <div>로딩 중...</div>;

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">{isNew ? '퀴즈 신규 등록' : '퀴즈 수정'}</h6>
        <div>
          <button type="button" className="btn btn-outline-info btn-sm mr-2" onClick={openPreview}>
            미리보기
          </button>
          <button className="btn btn-outline-secondary btn-sm mr-2" onClick={() => navigate('/quiz')}>
            목록으로
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      <div className="card-body">
        {message && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
            {message.text}
          </div>
        )}

        <div className="form-row">
          <div className="form-group col-md-3">
            <label>퀴즈 일자</label>
            <input type="date" className="form-control" name="quiz_date" value={quizSet.quiz_date} onChange={handleSetChange} />
          </div>
          <div className="form-group col-md-2">
            <label>언어</label>
            <select className="form-control" name="language" value={quizSet.language} onChange={handleSetChange}>
              {LANGUAGE_OPTIONS.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-2">
            <label>활성</label>
            <select className="form-control" name="is_active" value={quizSet.is_active} onChange={handleSetChange}>
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          </div>
          <div className="form-group col-md-3">
            <label>qt_ymd</label>
            <input
              type="text"
              className="form-control"
              name="qt_ymd"
              value={quizSet.qt_ymd}
              onChange={handleSetChange}
              maxLength={8}
              placeholder="YYYYMMDD"
            />
          </div>
        </div>

        <div className="form-group">
          <label>제목</label>
          <input type="text" className="form-control" name="title" value={quizSet.title} onChange={handleSetChange} />
        </div>
        <div className="form-group">
          <label>문제 출제 범위</label>
          <textarea
            className="form-control"
            name="source_scope"
            rows="2"
            value={quizSet.source_scope || ''}
            onChange={handleSetChange}
          />
        </div>
        <div className="form-group">
          <label>오답 공통 안내문구</label>
          <textarea
            className="form-control"
            name="wrong_guide_default"
            rows="2"
            value={quizSet.wrong_guide_default || ''}
            onChange={handleSetChange}
          />
        </div>

        <hr />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="font-weight-bold mb-0">문항 목록</h6>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddQuestion}>
            문항 추가
          </button>
        </div>

        {questions.length === 0 && <div className="text-muted mb-3">등록된 문항이 없습니다.</div>}

        {questions.map((question, index) => (
          <div className="card mb-3" key={question.id || question.tempId || `question-${index}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>문항 #{question.question_no || index + 1}</strong>
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveQuestion(index)}>
                  삭제
                </button>
              </div>

              <div className="form-row">
                <div className="form-group col-md-2">
                  <label>번호</label>
                  <input
                    type="number"
                    className="form-control"
                    value={question.question_no || index + 1}
                    onChange={(event) => handleQuestionChange(index, 'question_no', event.target.value)}
                  />
                </div>
                <div className="form-group col-md-2">
                  <label>유형</label>
                  <select
                    className="form-control"
                    value={question.question_type || 'OX'}
                    onChange={(event) => handleQuestionChange(index, 'question_type', event.target.value)}
                  >
                    {QUIZ_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>
                        {type === 'OX' ? 'OX' : '초성(INITIAL)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-8">
                  <label>정답</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.correct_answer || ''}
                    onChange={(event) => handleQuestionChange(index, 'correct_answer', event.target.value)}
                    placeholder={question.question_type === 'OX' ? 'O 또는 X' : '정답 텍스트'}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>문제</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={question.question_text || ''}
                  onChange={(event) => handleQuestionChange(index, 'question_text', event.target.value)}
                />
              </div>

              {question.question_type === 'INITIAL' && (
                <div className="form-group">
                  <label>초성 힌트</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.initial_hint || ''}
                    onChange={(event) => handleQuestionChange(index, 'initial_hint', event.target.value)}
                    placeholder="예: ㅇㄱ ㄱㅉㄱ"
                  />
                </div>
              )}

              <div className="form-group">
                <label>해설</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={question.explanation || ''}
                  onChange={(event) => handleQuestionChange(index, 'explanation', event.target.value)}
                />
              </div>
              <div className="form-group mb-0">
                <label>문항별 오답 안내문구 (비우면 공통 안내문구 사용)</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={question.wrong_guide || ''}
                  onChange={(event) => handleQuestionChange(index, 'wrong_guide', event.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {showPreview && (
        <QuizPreviewModal
          quizSet={quizSet}
          questions={questions}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default QuizSetDetail;
